import {
  Box,
  Drawer,
  Grid,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  Skeleton,
  Stack,
  Tooltip,
} from '@mui/material';
import Button from '@mui/material/Button';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../../../common/auth/PermissionsContext';
import { pushNotificationHandler } from '../../../common/PushNotification';
import RestHttpClient from '../../../common/RestHttpClient';
import { Icons } from '../../../component/icons/Icons';
import { GetDetailResponse } from '../../../generated/ApiClient';
import { formatToEuro } from '../../deals/new/finances/FinancesCalculationList';
import { OverlayContract } from '../detail/OverlayContract';
import { OverlayDissolveValues } from '../detail/OverlayDissolveValues';
import { OverlayDocuments } from '../detail/OverlayDocuments';
import { OverlayVehicle } from '../detail/OverlayVehicle';
import { isMboeRkv } from '../detail/OverlayDissolveValues.utils';
import { ReturnProcessPanel } from '../returnProcess/ReturnProcessPanel';
import { TypeCertificateForm } from '../typeCertificate/TypeCertificateForm';
import {
  useOverlayDetailQuery,
  useOverlayReportQuery,
  useOverlayRkvConfigQuery,
  useOverlayTypeCertificateQuery,
} from '../detail/useOverlayQueries';
import {
  OverlayContentSkeleton,
  OverlayFooterKpisSkeleton,
} from './DashboardApplicationOverlay.skeleton';

interface RightDrawerProps {
  isDrawerOpen: boolean;
  onClose: () => void;
  cmsId?: string;
  showExtend?: boolean;
}

const isCredit = (detail?: GetDetailResponse) => {
  return detail?.contract.financingType?.toLowerCase().includes('kredit');
};

const isLeasing = (detail?: GetDetailResponse) => {
  return detail?.contract.financingType?.toLowerCase().includes('leasing');
};

export function DashboardApplicationOverlay({
  isDrawerOpen,
  onClose,
  cmsId,
  showExtend,
}: RightDrawerProps) {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();

  const [active, setActive] = useState(0);
  const [pricesIncludingVAT, setPricesIncludingVAT] = useState<boolean>(false);
  const canViewDetail = hasPermission('Dashboard.DissolveDetails', 'View');

  const detailQuery = useOverlayDetailQuery(cmsId);
  const detailData = detailQuery.data;

  const reportQuery = useOverlayReportQuery(cmsId, !!detailData && !isCredit(detailData));
  const reportData = reportQuery.data;

  const rkvConfigQuery = useOverlayRkvConfigQuery(reportData?.rkv);
  const rkvConfiguration = rkvConfigQuery.data;

  const typeCertificateQuery = useOverlayTypeCertificateQuery(cmsId, detailData?.dealId);
  const typeCertificateData = typeCertificateQuery.data;

  useEffect(() => {
    if (isDrawerOpen) {
      setActive(0);
    }
  }, [isDrawerOpen, cmsId]);

  useEffect(() => {
    if (detailData?.configuration) {
      setPricesIncludingVAT(detailData.configuration.displayAsGross ?? false);
    }
  }, [detailData]);

  const handleSidebarClick = (i: number) => () => {
    setActive(i);
  };

  const handleExtendClick = async () => {
    if (detailData?.dealId) {
      RestHttpClient.extend(detailData.contractId, detailData?.dealId).then((_) => {
        pushNotificationHandler.publish(t('dashboard.contractsOverlay.extendedSuccess'), 'success');
      });
    }
  };

  const showVatSwitch = detailData?.contract.financingTypeId !== 'MI';
  const invalidDealId = !detailData?.dealId;

  const isLoading = detailQuery.isFetching || reportQuery.isFetching || rkvConfigQuery.isFetching;

  const showDissolveTab = !!detailData && !isCredit(detailData);
  const dissolvePending = !detailData;

  const showTypeCertificateTab = typeCertificateQuery.isFetched && !!typeCertificateData;
  const typeCertificatePending = !!detailData?.dealId && !typeCertificateQuery.isFetched;

  // Rückläuferprozess: nur für MBÖ-Fahrzeuge und nur, wenn es einen Deal gibt. Die MBÖ-Erkennung
  // hängt am Restwertgaranten aus dem Report — dieselbe Regel wie in den Auflösewerten.
  const showReturnProcessTab =
    hasPermission('ReturnProcess', 'View') && !!detailData?.dealId && isMboeRkv(reportData?.rkv);
  const returnProcessPending =
    !!detailData?.dealId && !isCredit(detailData) && !reportQuery.isFetched;

  return (
    <Drawer
      PaperProps={{
        sx: { height: '100vh', width: '65vw', position: 'fixed', bottom: 0, right: 0, top: 0 },
      }}
      anchor="right"
      open={isDrawerOpen}
      onClose={onClose}
    >
      <div className="dashboard-overlay">
        {isLoading && <LinearProgress />}
        <Grid container height="100%">
          <Grid item xs={2}>
            <Box className="sidebar">
              <List>
                <ListItemButton selected={active === 0} onClick={handleSidebarClick(0)}>
                  <ListItemText primary={t('dashboard.contractsOverlay.contract.label')} />
                </ListItemButton>
                {showDissolveTab && (
                  <ListItemButton selected={active === 1} onClick={handleSidebarClick(1)}>
                    <ListItemText primary={t('dashboard.contractsOverlay.dissolveValues.label')} />
                  </ListItemButton>
                )}
                {dissolvePending && (
                  <ListItemButton disabled>
                    <ListItemText primary={<Skeleton variant="text" width={120} />} />
                  </ListItemButton>
                )}
                <ListItemButton selected={active === 2} onClick={handleSidebarClick(2)}>
                  <ListItemText primary={t('dashboard.contractsOverlay.vehicle.label')} />
                </ListItemButton>

                <ListItemButton selected={active === 3} onClick={handleSidebarClick(3)}>
                  <ListItemText primary={t('dashboard.contractsOverlay.documents.label')} />
                </ListItemButton>

                {showTypeCertificateTab && (
                  <ListItemButton selected={active === 4} onClick={handleSidebarClick(4)}>
                    <ListItemText primary={t('dashboard.contractsOverlay.typeCertificate.label')} />
                  </ListItemButton>
                )}
                {typeCertificatePending && (
                  <ListItemButton disabled>
                    <ListItemText primary={<Skeleton variant="text" width={120} />} />
                  </ListItemButton>
                )}

                {showReturnProcessTab && (
                  <ListItemButton selected={active === 5} onClick={handleSidebarClick(5)}>
                    <ListItemText primary={t('dashboard.contractsOverlay.returnProcess.label')} />
                  </ListItemButton>
                )}
                {returnProcessPending && (
                  <ListItemButton disabled>
                    <ListItemText primary={<Skeleton variant="text" width={120} />} />
                  </ListItemButton>
                )}
              </List>
            </Box>
          </Grid>

          <Grid item xs={10}>
            <Box className="contracts-content">
              {detailData ? (
                <>
                  {!invalidDealId ? (
                    <h2>
                      #{cmsId}/{detailData.dealId} - {detailData.contract.customerName}{' '}
                    </h2>
                  ) : (
                    <h2>
                      #{cmsId} - {detailData.contract.customerName}{' '}
                    </h2>
                  )}
                  <div className="line" />

                  {active === 0 && <OverlayContract cmsId={cmsId} contract={detailData.contract} />}
                  {active === 1 && (
                    <OverlayDissolveValues
                      report={reportData}
                      cmsId={cmsId}
                      rkvConfiguration={rkvConfiguration}
                      canViewDetail={canViewDetail}
                      detailData={detailData}
                      showVatSwitch={showVatSwitch}
                      pricesIncludingVAT={pricesIncludingVAT}
                      onShowPricesIncludingVatChanged={(newValue: boolean) =>
                        setPricesIncludingVAT(newValue)
                      }
                    />
                  )}
                  {active === 2 && (
                    <OverlayVehicle
                      vehicle={detailData.vehicle}
                      isLeasing={isLeasing(detailData)}
                      canViewDetail={canViewDetail}
                      showVatSwitch={showVatSwitch}
                      dashboardConfiguration={detailData.configuration}
                      pricesIncludingVAT={pricesIncludingVAT}
                      onShowPricesIncludingVatChanged={(newValue: boolean) =>
                        setPricesIncludingVAT(newValue)
                      }
                    />
                  )}
                  {active === 3 && (
                    <OverlayDocuments
                      dealId={detailData.dealId}
                      documentDetail={detailData.document}
                    />
                  )}
                  {active === 4 && detailData.dealId && typeCertificateData && (
                    <TypeCertificateForm
                      dealId={detailData.dealId}
                      contractId={detailData.contractId}
                      data={typeCertificateData.data}
                    />
                  )}
                  {active === 5 && showReturnProcessTab && <ReturnProcessPanel cmsId={cmsId} />}
                </>
              ) : (
                <OverlayContentSkeleton />
              )}
            </Box>
          </Grid>
        </Grid>

        <div className="footer">
          <Button color="secondary" variant="outlined" onClick={onClose}>
            {t('dashboard.contractsOverlay.cancel')}
          </Button>

          {detailData ? (
            <>
              <div className="kpi-container">
                <h2>{formatToEuro(detailData.contract.prepayment)}</h2>
                <span>
                  {detailData.contract.isNet
                    ? `${t('dashboard.contractsOverlay.contract.downPayment')} ${t('price.exclVat')}`
                    : t('dashboard.contractsOverlay.contract.downPayment')}
                </span>
              </div>

              <div className="kpi-container">
                <h2>
                  {t('dashboard.contractsOverlay.contract.months', {
                    amount: detailData.contract.duration,
                  })}
                </h2>
                <span>{t('dashboard.contractsOverlay.contract.term')}</span>
              </div>

              <div className="kpi-container">
                <h2>
                  {`${
                    detailData.contract.mileage
                      ? detailData.contract.mileage.toLocaleString('de-DE')
                      : '-'
                  } km`}
                </h2>
                <span>{t('dashboard.contractsOverlay.contract.mileage')}</span>
              </div>

              <div className="kpi-container">
                <h2>{formatToEuro(detailData.contract.rate)}</h2>
                <span>
                  {detailData.contract.isNet
                    ? `${t('dashboard.contractsOverlay.rate')} ${t('price.exclVat')}`
                    : t('dashboard.contractsOverlay.rate')}
                </span>
              </div>
              <Stack flexDirection="row" gap={2}>
                {showExtend && detailData.contract.allowExtend && (
                  <Button onClick={handleExtendClick} color="secondary" variant="outlined">
                    {t('dashboard.contractsOverlay.extend')}
                  </Button>
                )}

                {invalidDealId ? (
                  <Tooltip
                    disableFocusListener
                    title={`${t('dashboard.contractsOverlay.invalidDealId')}: "${
                      detailData.dealReference ?? ''
                    }"`}
                  >
                    {Icons.alert()}
                  </Tooltip>
                ) : (
                  <Button
                    onClick={() => {
                      window.open(`${window.location.origin}/deals/${detailData.dealId}`, '_blank');
                    }}
                    color="primary"
                    variant="contained"
                  >
                    {t('dashboard.contractsOverlay.toDeal')}
                  </Button>
                )}
              </Stack>
            </>
          ) : (
            <OverlayFooterKpisSkeleton />
          )}
        </div>
      </div>
    </Drawer>
  );
}
